import { Plus } from "lucide-react";
import * as React from "react";

import { Badge } from "@/components/ui/badge";
import { DataTableButton } from "@/components/ui/data-table-button";
import {
  METRIC_TOOLTIP_ARROW_CLASS_NAME,
  METRIC_TOOLTIP_BODY_CLASS,
  metricTooltipShellClassName,
} from "@/components/ui/metric-tooltip";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useSimulatorPickLock } from "@/components/Reports/SimulatorPickLockContext";

/** Matches Trade Log buy / win gradient badges (shadcn Badge). */
const SIMULATOR_PICK_ADDED_BADGE_CLASS =
  "h-5 border-0 bg-transparent font-[family-name:var(--font-primary)] text-xs font-medium normal-case tracking-normal text-white shadow-none bg-[image:linear-gradient(135deg,#06E363,#AAFAB1)]";

/** Add-to-simulator column: plus icon until picked, then “Added” badge (click again to remove when onRemove is set). */
export function SimulatorPickActionCell(props: {
  picked: boolean;
  onAdd: () => void;
  onRemove?: () => void;
  addAriaLabel?: string;
  removeAriaLabel?: string;
}): React.ReactElement {
  const pickLock = useSimulatorPickLock();

  if (props.picked) {
    const removable = typeof props.onRemove === "function" && !pickLock.disabled;
    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            {removable ? (
              <DataTableButton
                type="button"
                variant="ghost"
                size="sm"
                className="mx-auto h-auto shrink-0 p-0 shadow-none hover:bg-transparent hover:opacity-85 active:opacity-75"
                aria-label={props.removeAriaLabel ?? "Remove from simulator picks"}
                onClick={(e) => {
                  e.stopPropagation();
                  props.onRemove?.();
                }}
              >
                <Badge data-simulator-pick-added-badge="" className={SIMULATOR_PICK_ADDED_BADGE_CLASS}>
                  Added
                </Badge>
              </DataTableButton>
            ) : (
              <span className="mx-auto inline-flex cursor-default">
                <Badge data-simulator-pick-added-badge="" className={SIMULATOR_PICK_ADDED_BADGE_CLASS}>
                  Added
                </Badge>
              </span>
            )}
          </TooltipTrigger>
          <TooltipContent
            side="top"
            arrowClassName={METRIC_TOOLTIP_ARROW_CLASS_NAME}
            className={cn(metricTooltipShellClassName("max-w-[240px]"), "z-[1200]")}
          >
            <p className={`text-[11px] leading-snug ${METRIC_TOOLTIP_BODY_CLASS}`}>
              {pickLock.disabled
                ? pickLock.reason
                : removable
                  ? "Click to remove from simulator picks"
                  : "Already added to simulator picks"}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (pickLock.disabled) {
    return (
      <TooltipProvider delayDuration={400}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className="mx-auto inline-flex h-6 w-6 cursor-not-allowed items-center justify-center opacity-40"
              aria-label={props.addAriaLabel ?? "Add to Simulator (locked)"}
              data-simulator-pick-locked=""
            >
              <Plus
                className="size-4 shrink-0 text-muted-foreground"
                strokeWidth={1.5}
                aria-hidden
              />
            </span>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            arrowClassName={METRIC_TOOLTIP_ARROW_CLASS_NAME}
            className={cn(metricTooltipShellClassName("max-w-[280px]"), "z-[1200]")}
          >
            <p className={`text-[11px] leading-snug ${METRIC_TOOLTIP_BODY_CLASS}`}>
              {pickLock.reason}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider delayDuration={400}>
      <Tooltip>
        <TooltipTrigger asChild>
          <DataTableButton
            type="button"
            variant="ghost"
            size="icon"
            className={cn(
              "mx-auto h-6 w-6 shrink-0 p-0 text-[color:var(--brand-tropical-surf)] shadow-none hover:bg-transparent hover:opacity-85 active:opacity-75",
            )}
            aria-label={props.addAriaLabel ?? "Add to Simulator"}
            onClick={(e) => {
              e.stopPropagation();
              props.onAdd();
            }}
          >
            <Plus className="size-4 shrink-0" strokeWidth={1.5} aria-hidden />
          </DataTableButton>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          arrowClassName={METRIC_TOOLTIP_ARROW_CLASS_NAME}
          className={cn(metricTooltipShellClassName("max-w-[260px]"), "z-[1200]")}
        >
          <p className={`text-[11px] leading-snug ${METRIC_TOOLTIP_BODY_CLASS}`}>
            Add this segment to the simulator picks list
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
